import React, { useState } from "react";
import { Download, X, Calendar, FileText, FileSpreadsheet, Globe } from "lucide-react";
import { PDF_LANGS } from "./pdfReceipt";

/**
 * Modal: pick period (7/30/90) + format (PDF/CSV) + language, then fire download.
 * onConfirm(days:number, format:"pdf"|"csv", lang:string) => Promise
 */
export default function ReportExportModal({ onClose, onConfirm }) {
  const [days, setDays] = useState(30);
  const [format, setFormat] = useState("pdf");
  const [lang, setLang] = useState("ru");
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    setLoading(true);
    try { await onConfirm(days, format, lang); }
    finally { setLoading(false); onClose(); }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
        zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      data-testid="report-export-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 460, background: "rgba(11,11,16,0.98)",
          border: "1px solid var(--tg-border)", borderRadius: 16, padding: 24, color: "var(--tg-text)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>📥 Экспорт отчёта</h3>
          <button onClick={onClose} className="dash-icon-btn" data-testid="report-modal-close"><X size={16} /></button>
        </div>

        {/* Period */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1, display: "flex", alignItems: "center", gap: 6 }}>
            <Calendar size={12} /> Период
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              [7,  "7 дней"],
              [30, "30 дней"],
              [90, "90 дней"],
            ].map(([d, label]) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`report-period-${d}`}
                style={{
                  flex: 1, padding: "10px 8px", borderRadius: 10,
                  border: "1px solid " + (days === d ? "var(--tg-blue)" : "var(--tg-border)"),
                  background: days === d ? "rgba(0,242,255,0.12)" : "transparent",
                  color: days === d ? "var(--tg-blue)" : "var(--tg-text-dim)",
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >{label}</button>
            ))}
          </div>
        </div>

        {/* Format */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Формат</div>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              ["pdf", <FileText size={14} />,        "PDF"],
              ["csv", <FileSpreadsheet size={14} />, "CSV (Excel)"],
            ].map(([v, ic, label]) => (
              <button
                key={v}
                onClick={() => setFormat(v)}
                data-testid={`report-format-${v}`}
                style={{
                  flex: 1, padding: "12px 8px", borderRadius: 10,
                  border: "1px solid " + (format === v ? "#FFD700" : "var(--tg-border)"),
                  background: format === v ? "rgba(255,215,0,0.1)" : "transparent",
                  color: format === v ? "#FFD700" : "var(--tg-text-dim)",
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >{ic} {label}</button>
            ))}
          </div>
        </div>

        {/* Language */}
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, color: "var(--tg-text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: 1, display: "flex", alignItems: "center", gap: 6 }}>
            <Globe size={12} /> Язык
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {PDF_LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`report-lang-${l.code}`}
                style={{
                  flex: 1, padding: "10px 8px", borderRadius: 10,
                  border: "1px solid " + (lang === l.code ? "var(--tg-blue)" : "var(--tg-border)"),
                  background: lang === l.code ? "rgba(0,242,255,0.12)" : "transparent",
                  color: lang === l.code ? "var(--tg-blue)" : "var(--tg-text-dim)",
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >{l.label}</button>
            ))}
          </div>
        </div>

        <button
          onClick={handle}
          disabled={loading}
          className="tg-btn gold"
          data-testid="report-modal-confirm"
          style={{ width: "100%", padding: "12px", fontSize: 14, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.6 : 1 }}
        >
          <Download size={16} /> {loading ? "Генерация…" : `Скачать ${format.toUpperCase()}`}
        </button>
      </div>
    </div>
  );
}
